import { useState } from 'react';

import { motion } from 'framer-motion';
import styled from 'styled-components';

import { Breadcrumb } from '@components/breadcrumb';
import { CancelSaveButtons } from '@components/cancel-save-buttons/cancel-save-buttons';
import { Card } from '@components/card';
import { FileButton } from '@components/file-button';
import { createForm, useCreateFormStore } from '@components/form';
import { Header } from '@components/header';
import { Spinner } from '@components/loading-spinner';
import { MaxLengthCounter } from '@components/max-length-counter';
import { PageContainers } from '@components/page-containers';
import { PageContent } from '@components/page-containers/page-container';
import { ImageViewer } from '@components/previewer';
import { Select } from '@components/select';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { DEFAULT_COMPANY_LOGO_DESKTOP_URL } from '@utils/assets';
import { pxToRem } from '@utils/converters';
import { isValidUrl, SMS_SENDER_NAME_MAX_LENGTH } from '@utils/validators';

import { useCompanyDetails, useCompanyTimezones, useEditCompanyDetails } from 'api/company/hooks';
import { Company } from 'api/company/requests';
import { useFeature } from 'ui/hooks';

export const General = () => {
  const { t } = useScopedTranslate(locale);
  const companyQuery = useCompanyDetails();
  const company = companyQuery.data;
  const { isEnabled: hasSMSFeature } = useFeature('FEATURE_SMS');

  if (!company) {
    return <Spinner />;
  }

  return (
    <>
      <Header breadcrumbs={<Breadcrumb items={[{ label: t('general') }]} />} />
      <PageContent>
        <PageContainers.MiddleColumnPage>
          <SettingsSection title={t('companyDetails')} hasSeparator isBordered>
            <CompanyDetails company={company} />
          </SettingsSection>
          <SettingsSection
            title={t('companyLogo')}
            description={t('logoDescription')}
            isBordered
            hasSeparator
          >
            <CompanyLogo company={company} />
          </SettingsSection>
          {hasSMSFeature && (
            <SettingsSection
              title={t('sms')}
              description={t('smsDescription')}
              hasSeparator
              isBordered
            >
              <CompanySmsSettings company={company} />
            </SettingsSection>
          )}
          <SettingsSection title={t('timezoneTitle')} description={t('timezoneDescription')}>
            <CompanyTimezoneSettings company={company} />
          </SettingsSection>
        </PageContainers.MiddleColumnPage>
      </PageContent>
    </>
  );
};

const DetailsForm = createForm<Company>();

const CompanyDetails = ({ company }: { company: Company }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const submitMutation = useEditCompanyDetails();

  const isDirty = formStore.useIsDirty();

  const handleSave = async (data: Omit<Company, 'logo'>) => {
    if (!data.websiteUrl) {
      return submitMutation.mutateAsync(data);
    }
    const websiteUrl = data.websiteUrl.match(/^https?:\/\//)
      ? data.websiteUrl
      : `https://${data.websiteUrl}`;
    if (!isValidUrl(websiteUrl)) {
      return formStore.actions.setErrors({ websiteUrl: 'valid-http-url' });
    }
    return submitMutation.mutateAsync({ ...data, websiteUrl });
  };

  return (
    <DetailsForm store={formStore} onSubmit={(data) => handleSave(data)}>
      <DetailsForm.TextField
        id="name"
        label={t('companyName')}
        validators={['required']}
        defaultValue={company.name}
      />
      <DetailsForm.TextField
        id="websiteUrl"
        key={company.websiteUrl}
        label={t('companyWebsite')}
        defaultValue={company.websiteUrl ?? ''}
      />

      {isDirty && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <DetailsForm.Actions>
            <DetailsForm.Reset text={t('core.cancel')} />
            <DetailsForm.Submit text={t('core.save')} variant="primary-blue" />
          </DetailsForm.Actions>
        </motion.div>
      )}
    </DetailsForm>
  );
};

const CompanyLogo = ({ company }: { company: Company }) => {
  const { t } = useScopedTranslate(locale);
  const [draft, setDraft] = useState<File | null>(null);
  const submitMutation = useEditCompanyDetails({
    onSuccess: () => setDraft(null),
  });

  const handleSubmit = async () => {
    const data = new FormData();
    if (draft) {
      data.append('logo', draft);
    }
    await submitMutation.mutateAsync(data);
  };

  return (
    <Stack orientation="vertical" space={4}>
      <ImageViewer
        url={
          draft ? URL.createObjectURL(draft) : (company.logo ?? DEFAULT_COMPANY_LOGO_DESKTOP_URL)
        }
        maxHeight={32}
      />
      <Stack space={2}>
        <div>
          <FileButton
            isReplace
            addText={t('addLogo')}
            replaceText={t('replaceLogo')}
            onFileSelected={setDraft}
            fileType="image"
          />
        </div>
        <Text
          font="regular14"
          color="textHelper"
          text={t('acceptedFiles', { files: '.jpg, .png & .gif' })}
        />
        <CancelSaveButtons
          isOpen={Boolean(draft)}
          onCancel={() => setDraft(null)}
          onSave={() => handleSubmit()}
          isDisabled={false}
        />
      </Stack>
    </Stack>
  );
};

type SmsData = {
  smsSenderName: string;
};

const SmsForm = createForm<SmsData>({ space: 1 });

const CompanySmsSettings = ({ company }: { company: Company }) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const submitMutation = useEditCompanyDetails();
  const fieldId = 'smsSenderName';

  const isDirty = formStore.useIsDirty();
  const hasFocus = formStore.useHasFocus();
  const currentValue = formStore.useFieldValue(fieldId) as string;
  const currentLength = currentValue?.length ?? 0;

  return (
    <SmsForm store={formStore} onSubmit={(data) => submitMutation.mutateAsync(data)}>
      <SmsForm.TextField
        id={fieldId}
        label={t('smsSenderName')}
        validators={['required', 'sms-sender-name']}
        defaultValue={company.smsSenderName}
        maxLength={SMS_SENDER_NAME_MAX_LENGTH}
      />

      {(isDirty || hasFocus) && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <Stack space={4}>
            <Stack space={2}>
              <MaxLengthCounter
                maxLength={SMS_SENDER_NAME_MAX_LENGTH}
                currentLength={currentLength}
              />
              <div>
                <Text text={t('smsRulesDescription')} color="textSecondary" />
                <Text text={t('smsRules')} color="textSecondary" font="regular14" />
              </div>
            </Stack>
            <SmsForm.Actions>
              <SmsForm.Reset text={t('core.cancel')} disabled={!isDirty} />
              <SmsForm.Submit text={t('core.save')} variant="primary-blue" disabled={!isDirty} />
            </SmsForm.Actions>
          </Stack>
        </motion.div>
      )}
    </SmsForm>
  );
};

const CompanyTimezoneSettings = ({ company }: { company: Company }) => {
  const { t } = useScopedTranslate(locale);
  const timezones = useCompanyTimezones();
  const submitMutation = useEditCompanyDetails();
  const fieldId = 'companyTimezone';
  const timezone = company?.timezone.split(' ')[1];
  const handleChange = (data: string) => {
    if (data !== timezone) {
      submitMutation.mutateAsync({ timezone: data });
    }
  };

  return (
    <StyledTimezoneContainer>
      <Select
        id={fieldId}
        label={t('timezone')}
        options={timezones.data ?? []}
        defaultValue={timezone}
        onChange={(data) => handleChange(data)}
        isClearable={false}
      />
    </StyledTimezoneContainer>
  );
};

type SectionProps = {
  description?: string;
  hasSeparator?: boolean;
  isBordered?: boolean;
  title?: string;
};

export const SettingsSection: React.FC<SectionProps> = ({
  children,
  description,
  hasSeparator,
  isBordered,
  title,
}) => {
  const Wrapper = isBordered ? Card : Stack;

  return (
    <>
      <Stack space={4}>
        <Stack space={2}>
          {title && <Text font="heading2" color="textPrimary" text={title} />}
          {description && <Text font="regular14" color="textSecondary" text={description} />}
        </Stack>
        <Wrapper space={6} variant="transparent">
          <Stack space={4}>{children}</Stack>
        </Wrapper>
      </Stack>
      {hasSeparator && <Separator margin={`${pxToRem(16)} 0`} />}
    </>
  );
};

const StyledTimezoneContainer = styled.div({
  maxWidth: pxToRem(368),
});

export const locale = {
  en: {
    general: 'General',
    companyDetails: 'Company details',
    companyLogo: 'Company logo',
    logoDescription: 'Your company logo is used in all outgoing email communication',
    noLogoAdded: 'No logo added',
    companyName: 'Company name',
    companyWebsite: 'Company website URL',
    addLogo: 'Add logo',
    replaceLogo: 'Replace logo',
    acceptedFiles: 'Accepted file types: {files}',
    sms: 'SMS',
    smsSenderName: 'SMS Sender name',
    smsDescription: 'Specify the Sender name you want for your SMS messages',
    smsRulesDescription: 'Due to telephone technology restraints the sms sender name may include',
    smsRules:
      ' • A maximum of 11 characters\n • Both upper-and lower-case Ascii letters and spaces\n • They may not be only numerals (e.g. "1984")',
    timezoneTitle: 'Default timezone',
    timezoneDescription:
      'This timezone controls all time-based events in the journey, including actions and scheduled polling, and will be used by default unless updated for a specific journey',
    timezone: 'Timezone',
  },
};
